import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AlertCircle, CheckCircle2, Loader2, RefreshCw, Star } from 'lucide-react';
import { apiService } from '../connectionsApiService';

export type TeamsTeam = { id: string; name: string };
export type TeamsChannel = { id: string; name: string };

type Props = {
  datastoreId?: string;
  /** Persisted default / previously selected team */
  defaultTeamId?: string;
  defaultChannelId?: string;
  defaultWebhookUrl?: string;
  /** Existing map or array from config */
  channelWebhooks?: Record<string, string> | Array<{ channelId: string; webhookUrl: string }>;
  onChange: (next: {
    channelWebhooks: Record<string, string>;
    defaultTeamId?: string;
    defaultChannelId?: string;
    defaultWebhookUrl?: string;
  }) => void;
};

const MASK = '********';

export function normalizeChannelWebhooks(
  input?: Record<string, string> | Array<{ channelId: string; webhookUrl: string }> | null
): Record<string, string> {
  if (!input) return {};
  if (Array.isArray(input)) {
    const map: Record<string, string> = {};
    input.forEach((row) => {
      if (row?.channelId) map[row.channelId] = row.webhookUrl || '';
    });
    return map;
  }
  return { ...input };
}

/**
 * Union-merge channels for one team into the global webhook map.
 * Preserves other teams' entries; prunes only channel ids previously known for this team.
 */
export function mergeChannelWebhooksForTeam(
  teamId: string,
  channels: TeamsChannel[],
  existing: Record<string, string>,
  channelsByTeamId: Record<string, string[]>
): { webhooks: Record<string, string>; channelsByTeamId: Record<string, string[]> } {
  const next = { ...existing };
  const previousIds = channelsByTeamId[teamId] || [];
  const nextIds = channels.map((ch) => ch.id);
  const nextIdSet = new Set(nextIds);

  previousIds.forEach((id) => {
    if (!nextIdSet.has(id)) delete next[id];
  });

  channels.forEach((ch) => {
    if (!(ch.id in next)) next[ch.id] = '';
  });

  return {
    webhooks: next,
    channelsByTeamId: { ...channelsByTeamId, [teamId]: nextIds },
  };
}

/** Drop mask placeholders; omit empty so null/empty webhooks are represented by absence. */
export function sanitizeWebhooksForSave(
  webhooks: Record<string, string>,
  prior?: Record<string, string>
): Record<string, string> {
  const out: Record<string, string> = {};
  Object.entries(webhooks).forEach(([id, url]) => {
    if (url === MASK) {
      const prev = prior?.[id];
      if (prev && prev !== MASK) out[id] = prev;
      return;
    }
    if (!url) return; // empty/null → omit
    out[id] = url;
  });
  return out;
}

const TeamsChannelsPanel: React.FC<Props> = ({
  datastoreId,
  defaultTeamId,
  defaultChannelId,
  defaultWebhookUrl,
  channelWebhooks,
  onChange,
}) => {
  const priorMap = useMemo(
    () => normalizeChannelWebhooks(channelWebhooks),
    [channelWebhooks]
  );
  const priorMapRef = useRef(priorMap);
  priorMapRef.current = priorMap;

  const [teams, setTeams] = useState<TeamsTeam[]>([]);
  const [selectedTeamId, setSelectedTeamId] = useState<string>(defaultTeamId || '');
  const [channels, setChannels] = useState<TeamsChannel[]>([]);
  const channelsByTeamIdRef = useRef<Record<string, string[]>>({});
  const [webhooks, setWebhooks] = useState<Record<string, string>>(() =>
    normalizeChannelWebhooks(channelWebhooks)
  );
  const [loadingTeams, setLoadingTeams] = useState(false);
  const [loadingChannels, setLoadingChannels] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [teamsLoaded, setTeamsLoaded] = useState(false);
  const [channelsLoadedForTeam, setChannelsLoadedForTeam] = useState<string | null>(null);
  const seededFromParent = useRef(false);
  const autoFetchTeamsAttempted = useRef(false);
  const initialChannelsFetchDone = useRef(false);

  // Seed once when parent config first provides webhooks (edit load)
  useEffect(() => {
    if (seededFromParent.current) return;
    if (Object.keys(priorMap).length === 0) return;
    seededFromParent.current = true;
    setWebhooks((prev) => ({ ...priorMap, ...prev }));
  }, [priorMap]);

  useEffect(() => {
    if (defaultTeamId && !selectedTeamId) {
      setSelectedTeamId(defaultTeamId);
    }
  }, [defaultTeamId, selectedTeamId]);

  const canFetch = Boolean(datastoreId);

  const pushToParent = (
    nextWebhooks: Record<string, string>,
    opts?: {
      defaultTeamId?: string;
      defaultChannelId?: string;
      defaultWebhookUrl?: string;
    }
  ) => {
    const persistMap = sanitizeWebhooksForSave(nextWebhooks, priorMapRef.current);
    const nextDefaultWebhook =
      opts && 'defaultWebhookUrl' in opts ? opts.defaultWebhookUrl : defaultWebhookUrl;
    const webhookForDefault =
      nextDefaultWebhook === MASK
        ? defaultWebhookUrl && defaultWebhookUrl !== MASK
          ? defaultWebhookUrl
          : undefined
        : nextDefaultWebhook;

    onChange({
      channelWebhooks: persistMap,
      defaultTeamId:
        opts && 'defaultTeamId' in opts ? opts.defaultTeamId : selectedTeamId || defaultTeamId,
      defaultChannelId:
        opts && 'defaultChannelId' in opts ? opts.defaultChannelId : defaultChannelId,
      defaultWebhookUrl: webhookForDefault,
    });
  };

  const fetchTeams = async () => {
    if (!datastoreId) return;
    setLoadingTeams(true);
    setError(null);
    try {
      const data = await apiService.get(`/DataStores/${datastoreId}/teams`, {}, 'backend');
      const list: TeamsTeam[] = Array.isArray(data?.teams) ? data.teams : [];
      setTeams(list);
      setTeamsLoaded(true);

      const preferred =
        (defaultTeamId && list.some((t) => t.id === defaultTeamId) && defaultTeamId) ||
        (selectedTeamId && list.some((t) => t.id === selectedTeamId) && selectedTeamId) ||
        list[0]?.id ||
        '';

      if (preferred) {
        setSelectedTeamId(preferred);
        pushToParent(webhooks, { defaultTeamId: preferred });
      }
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        'Failed to fetch Teams. Ensure the connection is saved and credentials are valid.';
      setError(msg);
    } finally {
      setLoadingTeams(false);
    }
  };

  const fetchChannels = async (teamIdOverride?: string) => {
    const teamId = teamIdOverride || selectedTeamId;
    if (!datastoreId || !teamId) return;
    setLoadingChannels(true);
    setError(null);
    try {
      const data = await apiService.get(
        `/DataStores/${datastoreId}/teams/channels`,
        { teamId },
        'backend'
      );
      const list: TeamsChannel[] = Array.isArray(data?.channels) ? data.channels : [];
      setChannels(list);
      setChannelsLoadedForTeam(teamId);

      setWebhooks((prev) => {
        const { webhooks: merged, channelsByTeamId: nextByTeam } = mergeChannelWebhooksForTeam(
          teamId,
          list,
          prev,
          channelsByTeamIdRef.current
        );
        channelsByTeamIdRef.current = nextByTeam;
        queueMicrotask(() =>
          pushToParent(merged, {
            defaultTeamId: teamId,
            defaultChannelId,
            defaultWebhookUrl,
          })
        );
        return merged;
      });
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        'Failed to fetch channels for the selected team.';
      setError(msg);
    } finally {
      setLoadingChannels(false);
    }
  };

  // Auto-fetch teams once when connection id is available
  useEffect(() => {
    if (!datastoreId || teamsLoaded || loadingTeams || autoFetchTeamsAttempted.current) return;
    autoFetchTeamsAttempted.current = true;
    void fetchTeams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [datastoreId]);

  // After teams load, auto-fetch channels once for the selected / default team
  useEffect(() => {
    if (
      !datastoreId ||
      !teamsLoaded ||
      !selectedTeamId ||
      loadingChannels ||
      initialChannelsFetchDone.current
    ) {
      return;
    }
    initialChannelsFetchDone.current = true;
    void fetchChannels(selectedTeamId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [datastoreId, teamsLoaded, selectedTeamId]);

  const onSelectTeam = (teamId: string) => {
    setSelectedTeamId(teamId);
    setChannels([]);
    setChannelsLoadedForTeam(null);
    pushToParent(webhooks, { defaultTeamId: teamId });
    if (teamId) void fetchChannels(teamId);
  };

  const setWebhook = (channelId: string, value: string) => {
    const next = { ...webhooks, [channelId]: value };
    setWebhooks(next);
    pushToParent(next);
  };

  const setAsDefault = (channel: TeamsChannel) => {
    const url = webhooks[channel.id];
    const resolvedUrl =
      !url || url === MASK
        ? defaultWebhookUrl && defaultWebhookUrl !== MASK
          ? defaultWebhookUrl
          : ''
        : url;
    pushToParent(webhooks, {
      defaultTeamId: selectedTeamId || defaultTeamId,
      defaultChannelId: channel.id,
      defaultWebhookUrl: resolvedUrl,
    });
  };

  const selectedTeamName = useMemo(
    () => teams.find((t) => t.id === selectedTeamId)?.name,
    [teams, selectedTeamId]
  );

  const defaultLabel = useMemo(() => {
    if (!defaultChannelId) return null;
    return channels.find((c) => c.id === defaultChannelId)?.name || defaultChannelId;
  }, [channels, defaultChannelId]);

  const channelsReady = channelsLoadedForTeam === selectedTeamId;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4">
        <h3 className="text-[11px] font-bold text-gray-400 uppercase tracking-[0.2em] whitespace-nowrap">
          Teams &amp; channel webhooks
        </h3>
        <div className="h-px w-full bg-neutral-800/60" />
      </div>

      <div className="rounded-xl border border-neutral-800 bg-neutral-800/20 p-4 space-y-3">
        <p className="text-xs text-gray-400 leading-relaxed">
          Save the connection, fetch teams, pick a team, then load its channels. Webhooks are optional.
          Switching teams keeps webhooks already set for other teams.
        </p>

        {!canFetch && (
          <div className="flex items-start gap-2 rounded-lg border border-amber-500/20 bg-amber-500/5 px-3 py-2 text-xs text-amber-300">
            <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            Save the connection first, then fetch teams and channels.
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            disabled={!canFetch || loadingTeams}
            onClick={fetchTeams}
            className="inline-flex items-center gap-2 rounded-lg border border-neutral-700 bg-neutral-900 px-3 py-2 text-xs font-semibold text-gray-200 hover:border-indigo-500/40 hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
          >
            {loadingTeams ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <RefreshCw className="h-3.5 w-3.5" />
            )}
            {teamsLoaded ? 'Refresh teams' : 'Fetch teams'}
          </button>

          {teamsLoaded && (
            <select
              value={selectedTeamId}
              onChange={(e) => onSelectTeam(e.target.value)}
              className="min-w-[200px] rounded-lg border border-neutral-700 bg-neutral-950 px-3 py-2 text-xs text-white focus:border-indigo-500 focus:outline-none"
            >
              <option value="">Select a team…</option>
              {teams.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          )}

          <button
            type="button"
            disabled={!canFetch || !selectedTeamId || loadingChannels}
            onClick={() => fetchChannels()}
            className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-3 py-2 text-xs font-semibold text-white hover:bg-indigo-500 disabled:cursor-not-allowed disabled:bg-neutral-800 disabled:text-gray-500"
          >
            {loadingChannels ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <RefreshCw className="h-3.5 w-3.5" />
            )}
            {channelsReady ? 'Refresh channels' : 'Fetch channels'}
          </button>

          {(selectedTeamName || defaultLabel) && (
            <span className="inline-flex items-center gap-1 rounded-md border border-indigo-500/30 bg-indigo-500/10 px-2 py-1 text-[10px] font-medium text-indigo-300">
              <Star className="h-3 w-3" />
              {selectedTeamName ? `Team: ${selectedTeamName}` : null}
              {selectedTeamName && defaultLabel ? ' · ' : null}
              {defaultLabel ? `Default channel: ${defaultLabel}` : null}
            </span>
          )}
        </div>

        {error && (
          <div className="rounded-lg border border-red-500/20 bg-red-500/5 px-3 py-2 text-xs text-red-400">
            {error}
          </div>
        )}

        {teamsLoaded && teams.length === 0 && !loadingTeams && !error && (
          <p className="text-xs text-gray-500">No teams returned for this connection.</p>
        )}

        {channelsReady && channels.length === 0 && !loadingChannels && !error && (
          <p className="text-xs text-gray-500">No channels returned for this team.</p>
        )}

        {channels.length > 0 && channelsReady && (
          <div className="overflow-x-auto rounded-lg border border-neutral-800">
            <table className="min-w-full text-left text-xs">
              <thead className="bg-neutral-950/80 text-gray-500">
                <tr>
                  <th className="px-3 py-2 font-semibold">Default</th>
                  <th className="px-3 py-2 font-semibold">Channel</th>
                  <th className="px-3 py-2 font-semibold">Channel ID</th>
                  <th className="px-3 py-2 font-semibold">Webhook URL (optional)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-800">
                {channels.map((ch) => {
                  const isDefault = defaultChannelId === ch.id;
                  return (
                    <tr key={ch.id} className="bg-neutral-900/40">
                      <td className="px-3 py-2.5">
                        <button
                          type="button"
                          title="Set as default channel"
                          onClick={() => setAsDefault(ch)}
                          className={`rounded-md p-1.5 transition ${
                            isDefault
                              ? 'bg-indigo-500/20 text-indigo-300'
                              : 'text-gray-500 hover:bg-neutral-800 hover:text-indigo-300'
                          }`}
                        >
                          <Star className={`h-3.5 w-3.5 ${isDefault ? 'fill-current' : ''}`} />
                        </button>
                      </td>
                      <td className="px-3 py-2.5 font-medium text-white">{ch.name}</td>
                      <td
                        className="px-3 py-2.5 font-mono text-[10px] text-gray-500 max-w-[140px] truncate"
                        title={ch.id}
                      >
                        {ch.id}
                      </td>
                      <td className="px-3 py-2.5 min-w-[180px]">
                        <input
                          type="password"
                          autoComplete="new-password"
                          value={webhooks[ch.id] ?? ''}
                          onChange={(e) => setWebhook(ch.id, e.target.value)}
                          onFocus={(e) => {
                            if (e.target.value === MASK) setWebhook(ch.id, '');
                          }}
                          placeholder="Optional — https://….webhook.office.com/…"
                          className="w-full rounded-md border border-neutral-700 bg-neutral-950 px-2 py-1.5 text-[11px] text-white placeholder:text-gray-600 focus:border-indigo-500 focus:outline-none"
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {channelsReady && !error && (
          <div className="flex items-center gap-1.5 text-[10px] text-emerald-400/80">
            <CheckCircle2 className="h-3 w-3" />
            Channels loaded for this team. Webhooks for other teams are preserved when you switch.
          </div>
        )}
      </div>
    </div>
  );
};

export default TeamsChannelsPanel;
